import { HttpContextToken, HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, switchMap, throwError } from 'rxjs';
import { TranslocoService } from '@jsverse/transloco';
import { NotificationService } from '@shared/services/notificacion/notification.service';
import { OWN_SESSION_REQUEST } from '@interceptors/own-session.context';
import { SUPPRESS_HTTP_ERRORS } from '@interceptors/error.interceptor';
import { IS_STOREFRONT_REQUEST } from './storefront-request.context';
import { CustomerSessionService } from './customer-session.service';

/** Cabecera con la que el backend elige la cadena de clientes cuando el navegador trae también cookies del panel. */
export const CUSTOMER_SURFACE_HEADER = 'X-Customer-Surface';
export const CUSTOMER_SURFACE_VALUE = 'STOREFRONT';

const IS_CUSTOMER_RETRY = new HttpContextToken<boolean>(() => false);

/**
 * Interceptor de la superficie de la tienda: declara la superficie, aísla la petición del panel
 * (`OWN_SESSION_REQUEST`, `SUPPRESS_HTTP_ERRORS`) y ante un 401 renueva la sesión de cliente (single-flight) y reintenta
 * una vez. Debe registrarse ANTES de `authInterceptor` (los tokens que fija solo los ven los interceptores posteriores).
 */
export const customerHttpInterceptor: HttpInterceptorFn = (req, next) => {
  if (!req.context.get(IS_STOREFRONT_REQUEST)) {
    return next(req);
  }
  const session = inject(CustomerSessionService);
  const notification = inject(NotificationService);
  const transloco = inject(TranslocoService);

  const tag = (r: typeof req, retry: boolean) => r.clone({
    setHeaders: { [CUSTOMER_SURFACE_HEADER]: CUSTOMER_SURFACE_VALUE },
    withCredentials: true,
    context: r.context.set(OWN_SESSION_REQUEST, true).set(SUPPRESS_HTTP_ERRORS, true).set(IS_CUSTOMER_RETRY, retry)
  });
  const isAuthPath = req.url.includes('/storefront/customer-auth/');

  return next(tag(req, req.context.get(IS_CUSTOMER_RETRY))).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status !== 401 || req.context.get(IS_CUSTOMER_RETRY) || isAuthPath) {
        return throwError(() => error);
      }
      return session.refresh().pipe(
        // catchError PEGADO al refresh: si el reintento falla por su cuenta, la sesión recién renovada sigue siendo válida.
        catchError(() => {
          const eraInvitado = session.hasSession() && session.isGuest();
          session.clearLocalSession();
          if (eraInvitado) {
            notification.warning(
              transloco.translate('storefront.guestSessionExpired.title'),
              transloco.translate('storefront.guestSessionExpired.message')
            );
          }
          return throwError(() => error);
        }),
        switchMap(() => next(tag(req, true)))
      );
    })
  );
};
